/**
 * GA4 + first-party funnel analytics for UserDashboard.
 *
 * Two layers:
 * 1. GA4 — Advanced Consent Mode v2: tag loads immediately, cookieless under
 *    the denied defaults set by the inline index.html tag. Consent is owned
 *    by the RnkCookie CMP (2026-08-09): its core natively pushes
 *    gtag('consent','update', …) on decision AND re-applies it on load for
 *    returning visitors — this module must never push consent state itself.
 * 2. First-party funnel beacon — fires always, anonymous, no cookies needed.
 *
 * Identity model: anonymous session chaining only.
 * - journey_id: seeded from URL ?jid= param (set by WebUi), stored in sessionStorage.
 * - No persistent visitor cookie. No repeat-visitor tracking.
 *
 * Bot filtering: skips beacons for headless browsers, pre-renderers, known bots.
 */

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const JID_SESSION_KEY = 'rnk_funnel_jid';

const GA_STRIP_PARAMS = new Set([
  'jid', 'source', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
]);

function sanitizeGaPath(raw: string): string {
  const qIdx = raw.indexOf('?');
  if (qIdx === -1) return raw;
  const path = raw.slice(0, qIdx);
  const params = new URLSearchParams(raw.slice(qIdx));
  GA_STRIP_PARAMS.forEach(k => params.delete(k));
  const remaining = params.toString();
  return remaining ? `${path}?${remaining}` : path;
}

// ── Bot detection ───────────────────────────────────────────────────────

const isBot = (() => {
  if (typeof navigator === 'undefined') return true;
  if ((navigator as any).webdriver) return true;
  const ua = navigator.userAgent.toLowerCase();
  if (/headlesschrome|prerender|lighthouse|googlebot|bingbot|yandexbot|baiduspider|slurp|duckduckbot|facebookexternalhit|twitterbot|linkedinbot|semrushbot|ahrefsbot|mj12bot|dotbot|screaming frog|vercelbot/.test(ua)) return true;
  return false;
})();

// ── Env / backend URL ───────────────────────────────────────────────────

const isDev = import.meta.env.DEV;
const configuredApi = import.meta.env.VITE_API_URL as string | undefined;

// VITE_API_URL typically ends with /api (for apiClient proxy pattern).
// Funnel route is mounted at /funnel/event (no /api prefix), so strip it.
const FUNNEL_URL: string | null = (() => {
  if (isBot) return null;
  if (configuredApi) {
    const base = configuredApi.replace(/\/api\/?$/, '');
    return `${base}/funnel/event`;
  }
  if (isDev) return 'http://localhost:3001/funnel/event';
  return null;
})();

// ── GA4 init (Advanced Consent Mode v2) ─────────────────────────────────

let initialized = false;

/** Ensure the gtag function and dataLayer exist (idempotent). The inline
 *  index.html consent-default tag normally creates both before we run —
 *  reuse them, never redefine incompatibly. */
function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    // CRITICAL: Must push the raw `arguments` object, NOT a rest-param array.
    // gtag.js only recognises Arguments objects when draining the dataLayer queue.
    // @ts-expect-error - gtag.js requires the raw Arguments object
    // eslint-disable-next-line prefer-rest-params
    window.gtag = function () { window.dataLayer.push(arguments); };
  }
}

/**
 * Load gtag.js + configure GA4. Loads unconditionally — Consent Mode v2
 * denied defaults (set by the inline index.html tag; NOT re-pushed here)
 * keep it cookieless until RnkCookie grants analytics_storage natively.
 * Degrades silently when VITE_GA_MEASUREMENT_ID is unset (dev).
 */
export function initGA4(): void {
  if (initialized || !GA_ID) return;
  if (document.querySelector('script[src*="googletagmanager"]')) {
    initialized = true;
    return;
  }

  initialized = true;
  ensureGtag();

  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  script.async = true;
  // Consent-mode-governed: the RnkCookie autoblocker intercepts ALL script
  // insertions — without this attribute the GA tag gets blocked as
  // uncategorised. Must be set BEFORE appendChild.
  script.setAttribute('data-rnk-ignore', '');
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', GA_ID, {
    linker: { domains: ['rnkrocket.com', 'app.rnkrocket.com'] },
  });
}

export function trackPageView(path: string): void {
  if (!initialized || !GA_ID) return;
  if (typeof window.gtag === 'function') {
    window.gtag('config', GA_ID, { page_path: sanitizeGaPath(path) });
  }
}

export function trackEvent(name: string, params?: Record<string, string | number | boolean>): void {
  if (!initialized || !GA_ID) return;
  if (typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  }
}

// ── Anonymous journey ID (cross-domain, sessionStorage-backed) ──────────

// Scanned URL handed over from the marketing-site free scan (?scanned_url=)
// so onboarding can pre-fill the project URL the visitor just scanned.
const SCANNED_URL_KEY = 'rnk_scanned_url';

function seedJourneyFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    let changed = false;

    const urlJid = params.get('jid');
    if (urlJid && urlJid.length <= 36) {
      sessionStorage.setItem(JID_SESSION_KEY, urlJid);
      params.delete('jid');
      changed = true;
    }

    const urlScanned = params.get('scanned_url');
    if (urlScanned && urlScanned.length <= 2048) {
      sessionStorage.setItem(SCANNED_URL_KEY, urlScanned);
      params.delete('scanned_url');
      changed = true;
    }

    if (changed) {
      const remaining = params.toString();
      const cleanUrl = window.location.pathname + (remaining ? `?${remaining}` : '') + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
    }
  } catch {
    // sessionStorage or history may be unavailable
  }
}

// Run once on module load
seedJourneyFromUrl();

function getJourneyId(): string {
  try {
    const stored = sessionStorage.getItem(JID_SESSION_KEY);
    if (stored) return stored;
  } catch {
    // sessionStorage unavailable
  }
  const generated = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  try {
    sessionStorage.setItem(JID_SESSION_KEY, generated);
  } catch {
    // best-effort
  }
  return generated;
}

// ── UTM params vs internal entrypoint ───────────────────────────────────
//
// Kept separate on the wire so acquisition reporting stops being polluted
// by internal CTA labels:
//
//   - source/medium/campaign: true acquisition, ONLY from utm_* params.
//   - entrypoint: internal CTA origin, ONLY from the `source` URL param
//     stamped by WebUi on outbound links ("paid_social_lp", "webui_pricing",
//     "local-seo-bristol", etc). Never conflated with utm_source.
//
// Both values are captured ONCE at module load from the URL the user
// arrived on (post-cross-domain-hop from WebUi) and stay sticky for the
// tab-session lifetime. Internal SPA navigation can and does rewrite the
// URL via react-router, stripping these params — caching at load time
// means the first beacon (and every subsequent beacon) still carries the
// original acquisition even if nav happened before the first send.

const utmCache: Record<string, string> = (() => {
  if (typeof window === 'undefined') {
    return { source: '', medium: '', campaign: '' };
  }
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      source: params.get('utm_source') || '',
      medium: params.get('utm_medium') || '',
      campaign: params.get('utm_campaign') || '',
    };
  } catch {
    return { source: '', medium: '', campaign: '' };
  }
})();

function getUtmParams(): Record<string, string> {
  return utmCache;
}

const entrypointCache: string = (() => {
  if (typeof window === 'undefined') return '';
  try {
    return new URLSearchParams(window.location.search).get('source') || '';
  } catch {
    return '';
  }
})();

function getEntrypoint(): string {
  return entrypointCache;
}

// ── First-party funnel beacon ───────────────────────────────────────────

export function trackFunnelEvent(event: string, page: string, label?: string): void {
  if (!FUNNEL_URL) return;
  try {
    const utm = getUtmParams();
    const entrypoint = getEntrypoint();
    const body = JSON.stringify({
      event,
      page,
      label,
      source: utm.source,       // true acquisition (utm_source only)
      medium: utm.medium,
      campaign: utm.campaign,
      entrypoint,               // internal CTA origin (?source= param)
      journey_id: getJourneyId(),
      referrer: document.referrer || undefined,
    });
    fetch(FUNNEL_URL, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      mode: 'cors',
    }).catch(() => {});
  } catch {
    // Never block the user
  }
}

// ── Engagement intelligence: form drop-off visibility ───────────────────
//
// Mirrors the WebUi helpers in RnkRocket.WebUi/src/utils/tracking.ts so the
// admin dashboard can group form drop-off events by the same `formSource`
// label regardless of which app domain fired them. Use the SAME constant
// per form ('register', 'login', ...) so a single filter catches every step.
//
// Privacy envelope: the `label` field must never contain user-entered
// values. Only field names and reason codes. Enforced by call sites.

export function trackFormFieldFocus(formSource: string, fieldName: string): void {
  trackFunnelEvent(
    'form_field_focus',
    typeof window !== 'undefined' ? window.location.pathname : '/',
    `${formSource}:${fieldName}`,
  );
}

export function trackFormSubmitStart(formSource: string): void {
  trackFunnelEvent(
    'form_submit_start',
    typeof window !== 'undefined' ? window.location.pathname : '/',
    formSource,
  );
}

export function trackFormSubmitSuccess(formSource: string): void {
  trackFunnelEvent(
    'form_submit_success',
    typeof window !== 'undefined' ? window.location.pathname : '/',
    formSource,
  );
}

export function trackFormSubmitError(formSource: string, errorReason: string): void {
  trackFunnelEvent(
    'form_submit_error',
    typeof window !== 'undefined' ? window.location.pathname : '/',
    `${formSource}:${errorReason}`,
  );
}

export function trackFormAbandon(formSource: string, lastFieldFocused: string): void {
  trackFunnelEvent(
    'form_abandon',
    typeof window !== 'undefined' ? window.location.pathname : '/',
    `${formSource}:${lastFieldFocused || 'unknown'}`,
  );
}

// ── Checkout lifecycle: paid + bounced ──────────────────────────────────
//
// `purchase_completed` is also fired server-side from the Stripe webhook
// (canonical source — survives tab close). The client-side fire is belt-
// and-braces for immediate UI confirmation. Both writes use the same
// journey_id so the journey row only gets one closing event in practice
// (the second write is a no-op duplicate at the SQL level — acceptable).
//
// `checkout_abandoned` is client-side ONLY — Stripe doesn't notify us when
// a user bounces, so the cancel_url return is the only signal we get.

export function trackPurchaseCompleted(label?: string): void {
  trackFunnelEvent(
    'purchase_completed',
    typeof window !== 'undefined' ? window.location.pathname : '/',
    label,
  );
}

export function trackCheckoutAbandoned(label?: string): void {
  trackFunnelEvent(
    'checkout_abandoned',
    typeof window !== 'undefined' ? window.location.pathname : '/',
    label,
  );
}
